/**
 * Report Sharing - Manage report access and collaboration
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { shareReport, unshareReport, getReportShares } from '../../api/reports';
import {
  Share2,
  Mail,
  X,
  Shield,
  Eye,
  Edit,
  Crown,
  UserPlus,
  UserMinus,
  Users,
} from 'lucide-react';

interface ReportSharingProps {
  reportId: string;
}

export function ReportSharing({ reportId }: ReportSharingProps) {
  const [newUserEmail, setNewUserEmail] = useState('');
  const [selectedPermission, setSelectedPermission] = useState<'view' | 'edit' | 'admin'>('view');
  const queryClient = useQueryClient();

  const { data: shares, isLoading } = useQuery({
    queryKey: ['report-shares', reportId],
    queryFn: () => getReportShares(reportId),
    enabled: !!reportId,
  });

  const shareMutation = useMutation({
    mutationFn: ({ emails, permission }: { emails: string[]; permission: 'view' | 'edit' | 'admin' }) =>
      shareReport(reportId, emails, permission),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report-shares', reportId] });
      setNewUserEmail('');
    },
  });

  const unshareMutation = useMutation({
    mutationFn: (emails: string[]) => unshareReport(reportId, emails),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report-shares', reportId] });
    },
  });

  const handleShare = () => {
    if (!newUserEmail.trim()) return;
    const emails = newUserEmail.split(',').map(e => e.trim()).filter(e => e);
    shareMutation.mutate({ emails, permission: selectedPermission });
  };

  const handleUnshare = (userEmail: string) => {
    if (window.confirm(`Remove access for ${userEmail}?`)) {
      unshareMutation.mutate([userEmail]);
    }
  };

  const permissionIcons = {
    view: Eye,
    edit: Edit,
    admin: Crown,
  };

  const permissionColors = {
    view: 'bg-blue-100 text-blue-700',
    edit: 'bg-green-100 text-green-700',
    admin: 'bg-purple-100 text-purple-700',
  };

  return (
    <Card title="Share Report">
      <div className="space-y-6">
        {/* Add User Form */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                User Email(s)
              </label>
              <input
                type="text"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                placeholder="user@example.com, user2@example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Separate multiple emails with commas
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Permission Level
              </label>
              <div className="grid grid-cols-3 gap-2">
                {(['view', 'edit', 'admin'] as const).map((perm) => {
                  const Icon = permissionIcons[perm];
                  return (
                    <button
                      key={perm}
                      onClick={() => setSelectedPermission(perm)}
                      className={`px-4 py-3 border-2 rounded-lg transition-all flex items-center justify-center gap-2 ${
                        selectedPermission === perm
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      <span className="capitalize font-medium">{perm}</span>
                    </button>
                  );
                })}
              </div>
              <div className="mt-2 text-xs text-gray-500">
                {selectedPermission === 'view' && '• Can view report and download'}
                {selectedPermission === 'edit' && '• Can view, edit, and create versions'}
                {selectedPermission === 'admin' && '• Can view, edit, share, and delete'}
              </div>
            </div>

            <Button
              onClick={handleShare}
              disabled={!newUserEmail.trim() || shareMutation.isPending}
              className="w-full"
            >
              <UserPlus className="h-4 w-4" />
              Share Report
            </Button>
          </div>
        </div>

        {/* Current Shares */}
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
            <Users className="h-4 w-4" />
            People with Access ({shares?.length || 0})
          </h3>
          
          {isLoading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : shares && shares.length > 0 ? (
            <div className="space-y-2">
              {shares.map((share) => {
                const Icon = permissionIcons[share.permission];
                return (
                  <div
                    key={share.user_id}
                    className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="bg-blue-100 text-blue-700 rounded-full w-10 h-10 flex items-center justify-center font-medium">
                        {share.user_email.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{share.user_email}</div>
                        <div className="text-xs text-gray-500">
                          Shared {new Date(share.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${permissionColors[share.permission]}`}>
                        <Icon className="h-3 w-3" />
                        {share.permission}
                      </span>
                      <button
                        onClick={() => handleUnshare(share.user_email)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Remove access"
                      >
                        <UserMinus className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
              <Share2 className="h-8 w-8 mx-auto mb-2 text-gray-400" />
              <p>This report hasn't been shared with anyone yet</p>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

